import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import PromoBanner from "@/components/store/PromoBanner";
import StoreFilters from "@/components/store/StoreFilters";
import ProductCard from "@/components/store/ProductCard";
import RecentlyViewed from "@/components/store/RecentlyViewed";
import CartDrawer from "@/components/store/CartDrawer";
import StickyCartButton from "@/components/store/StickyCartButton";
import { useLanguage } from "@/context/LanguageContext";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import { useStock } from "@/context/StockContext";
import {
  PRODUCTS,
  computeDiscountedPrice,
  getProductDiscountPct,
  isAvailable,
  localized,
} from "@/data/products";
import { trackEvent } from "@/lib/analytics";

const FILTERS_KEY = "bob_store_filters";
const RETURN_KEY = "bob_store_return";
const RETURN_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

const readStoredFilters = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

// Read (but DO NOT remove) the return marker. The marker is removed only after
// the scroll is actually applied (inside requestAnimationFrame), which makes
// this safe for React 18 StrictMode double-mounts in development.
const peekReturnMarker = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    if (Date.now() - (parsed.ts || 0) > RETURN_MAX_AGE_MS) {
      sessionStorage.removeItem(RETURN_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export default function StockPage() {
  const { t, lang } = useLanguage();
  const S = t.store;

  const stored = readStoredFilters();
  const [category, setCategory] = useState(stored?.category || "all");
  const [search, setSearch] = useState(stored?.search || "");
  const [availableOnly, setAvailableOnly] = useState(stored?.availableOnly || false);
  const [sortBy, setSortBy] = useState(stored?.sortBy || "default");
  const [highlightId, setHighlightId] = useState(null);
  const previousSearchRef = useRef(search);
  const previousCategoryRef = useRef(category);
  const previousAvailableOnlyRef = useRef(availableOnly);
  const previousSortRef = useRef(sortBy);
  const viewTrackedRef = useRef(false);

  const { version: catalogVersion } = useCatalog();
  const { stockMap, loaded: stockLoaded } = useStock();
  const productAvailable = (p) => isAvailable(p, stockLoaded ? stockMap[p.id] : undefined);

  const visibleProducts = useMemo(
    () => PRODUCTS.filter((p) => !isHiddenById(p.id)),
    [catalogVersion]
  );

  const availableCount = useMemo(
    () => visibleProducts.filter((p) => productAvailable(p)).length,
    [visibleProducts, stockLoaded, stockMap]
  );

  const filtered = useMemo(() => {
    let list = visibleProducts.slice();

    if (category === "authentic") {
      list = list.filter((p) => p.authentic === true);
    } else if (category !== "all") {
      list = list.filter((p) => p.category === category);
    }
    if (availableOnly) list = list.filter((p) => productAvailable(p));

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const idStr = `#${p.id}`.toLowerCase();
        const name = localized(p.name, lang).toLowerCase();
        return idStr.includes(q) || p.id.includes(q.replace(/[#]/g, "")) || name.includes(q);
      });
    }

    if (sortBy === "priceLow") {
      list.sort((a, b) => {
        const ap = computeDiscountedPrice(a.originalPrice, getProductDiscountPct(a));
        const bp = computeDiscountedPrice(b.originalPrice, getProductDiscountPct(b));
        if (ap == null && bp == null) return 0;
        if (ap == null) return 1;
        if (bp == null) return -1;
        return ap - bp;
      });
    } else if (sortBy === "priceHigh") {
      list.sort((a, b) => {
        const ap = computeDiscountedPrice(a.originalPrice, getProductDiscountPct(a));
        const bp = computeDiscountedPrice(b.originalPrice, getProductDiscountPct(b));
        if (ap == null && bp == null) return 0;
        if (ap == null) return 1;
        if (bp == null) return -1;
        return bp - ap;
      });
    }

    return list;
  }, [visibleProducts, category, availableOnly, search, sortBy, lang, stockLoaded, stockMap]);

  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackEvent("store_view", {
      routeName: "store",
      metadata: {
        visible_products: visibleProducts.length,
        available_products: availableCount,
      },
    });
  }, [availableCount, visibleProducts.length]);

  useEffect(() => {
    const trimmed = search.trim();
    if (previousSearchRef.current === search) return undefined;
    previousSearchRef.current = search;
    if (!trimmed) return undefined;
    const timer = window.setTimeout(() => {
      trackEvent("product_search", {
        routeName: "store",
        searchQuery: trimmed,
        metadata: { scope: "store", results_count: filtered.length },
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [filtered.length, search]);

  useEffect(() => {
    if (previousCategoryRef.current === category) return;
    previousCategoryRef.current = category;
    trackEvent("product_filter", {
      routeName: "store",
      metadata: { scope: "store", filter: "category", value: category, results_count: filtered.length },
    });
  }, [category, filtered.length]);

  useEffect(() => {
    if (previousAvailableOnlyRef.current === availableOnly) return;
    previousAvailableOnlyRef.current = availableOnly;
    trackEvent("product_filter", {
      routeName: "store",
      metadata: {
        scope: "store",
        filter: "available_only",
        value: availableOnly ? "on" : "off",
        results_count: filtered.length,
      },
    });
  }, [availableOnly, filtered.length]);

  useEffect(() => {
    if (previousSortRef.current === sortBy) return;
    previousSortRef.current = sortBy;
    trackEvent("product_sort", {
      routeName: "store",
      metadata: { scope: "store", sort: sortBy, results_count: filtered.length },
    });
  }, [filtered.length, sortBy]);

  // Persist filter state so it survives navigation to product details and back
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(
        FILTERS_KEY,
        JSON.stringify({ category, search, availableOnly, sortBy })
      );
    } catch {
      // Ignore quota / privacy mode errors
    }
  }, [category, search, availableOnly, sortBy]);

  // Restore scroll position + highlight the previously viewed product card
  useEffect(() => {
    const marker = peekReturnMarker();
    if (!marker) return;

    let raf1 = 0;
    let raf2 = 0;
    let highlightTimer = 0;

    // Wait two animation frames so the grid has laid out (framer-motion + images)
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        let scrolled = false;
        if (marker.productId) {
          // Scope the lookup to the container the user originally clicked from
          // so the Recently Viewed strip doesn't shadow the main grid card
          // (both share the same data-testid).
          const containerSelector =
            marker.source === "recent"
              ? '[data-testid="recently-viewed-track"]'
              : '[data-testid="stock-grid"]';
          const container = document.querySelector(containerSelector);
          const cardSelector = `[data-testid="product-card-${marker.productId}"]`;
          const target =
            (container && container.querySelector(cardSelector)) ||
            (marker.source !== "grid"
              ? document
                  .querySelector('[data-testid="stock-grid"]')
                  ?.querySelector(cardSelector)
              : null);
          if (target) {
            const rect = target.getBoundingClientRect();
            const offset = window.innerWidth < 768 ? 90 : 110; // clear sticky navbar
            const y = (window.scrollY || window.pageYOffset || 0) + rect.top - offset;
            window.scrollTo({ top: Math.max(0, y), behavior: "instant" });
            setHighlightId(marker.productId);
            highlightTimer = window.setTimeout(() => setHighlightId(null), 1800);
            scrolled = true;
          }
        }
        if (!scrolled && typeof marker.scrollY === "number") {
          window.scrollTo({ top: marker.scrollY, behavior: "instant" });
        }
        try {
          sessionStorage.removeItem(RETURN_KEY);
        } catch {
          // Ignore
        }
      });
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (highlightTimer) clearTimeout(highlightTimer);
    };
  }, [filtered]);

  return (
    <main
      className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] dark:bg-[#080808] dark:text-[#F6F7FB]"
      data-testid="stock-page"
    >
      <Navbar />

      {/* Hero / Banner */}
      <section className="px-5 pt-24 md:px-8 md:pt-28">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 rounded-[30px] border border-black/6 bg-white px-6 py-7 shadow-[0_20px_50px_rgba(16,24,40,0.06)] sm:px-8 md:mb-8 md:px-10 md:py-9 dark:border-white/10 dark:bg-[#121212]">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <span className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#F37021]/20 bg-[#FFF3EA] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#C95D13] dark:bg-[#1A1A1A] dark:text-[#F8A056]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F37021]" />
                  {S.eyebrow}
                </span>
                <h1
                  className="font-display text-4xl font-bold leading-[1.02] text-[#111] sm:text-5xl md:text-6xl dark:text-white"
                  data-testid="stock-page-title"
                >
                  {S.pageTitle}
                </h1>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#555] sm:text-lg dark:text-[#BAC4D4]">
                  {S.pageSubtitle}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
                <div className="rounded-[22px] border border-black/6 bg-[#FAFAFA] px-4 py-3 dark:border-white/10 dark:bg-[#121212]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#888] dark:text-[#8FA1B8]">
                    {S.categoryAll}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-[#111] dark:text-white">
                    {visibleProducts.length}
                  </div>
                </div>
                <div className="rounded-[22px] border border-black/6 bg-[#FAFAFA] px-4 py-3 dark:border-white/10 dark:bg-[#121212]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#888] dark:text-[#8FA1B8]">
                    {S.availableOnly}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-[#111] dark:text-white">
                    {availableCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PromoBanner />
        </div>
      </section>

      <RecentlyViewed />

      {/* Filters */}
      <section className="px-5 pt-10 md:px-8 md:pt-14">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-[30px] border border-black/6 bg-white p-4 shadow-[0_18px_45px_rgba(16,24,40,0.05)] md:p-5 dark:border-white/10 dark:bg-[#121212]">
            <StoreFilters
              category={category}
              setCategory={setCategory}
              search={search}
              setSearch={setSearch}
              availableOnly={availableOnly}
              setAvailableOnly={setAvailableOnly}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-5 pb-20 pt-8 md:px-8 md:pb-28 md:pt-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-2xl font-semibold text-[#111] sm:text-[32px] dark:text-white">
                {S.pageTitle}
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-[#111] px-3 py-1.5 text-sm font-medium text-white">
                  {filtered.length}
                </span>
                {search.trim() ? (
                    <span className="inline-flex items-center rounded-full border border-[#F37021]/20 bg-[#FFF6EF] px-4 py-1.5 text-sm text-[#A5531D] dark:bg-[#1A1A1A] dark:text-[#F8A056]">
                    "{search.trim()}"
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div
              className="rounded-[30px] border border-dashed border-black/10 bg-white px-6 py-20 text-center text-[#666] shadow-[0_18px_45px_rgba(16,24,40,0.04)] dark:border-white/10 dark:bg-[#121212] dark:text-[#BAC4D4]"
              data-testid="stock-empty-state"
            >
              <div className="mb-3 text-5xl text-[#F37021]">0</div>
              <p className="text-lg font-medium">{S.noResults}</p>
              <p className="mt-2 text-sm text-[#888]">{S.noResultsHint}</p>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.04 } },
              }}
              className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-2 sm:gap-x-5 md:grid-cols-3 md:gap-x-6 md:gap-y-10 lg:grid-cols-4"
              data-testid="stock-grid"
            >
              {filtered.map((p) => (
                <motion.div
                  key={p.id}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                  }}
                >
                  <ProductCard product={p} highlight={highlightId === p.id} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <CartDrawer />
      <StickyCartButton />
    </main>
  );
}
